import { createMuiTheme } from '@material-ui/core/styles';

const textColorLight = '#666666';
const textColorDark = '#333333';

const theme = createMuiTheme({
  breakpoints: {
    values: {
      xs: 0,
      sm: 640,
      md: 960,
      lg: 1280,
      xl: 1400,
    },
  },
  palette: {
    contrastThreshold: 3,
    tonalOffset: 0.2,
    primary: {
      light: '#4d90fe',
      main: '#3FA333', // emerald green
      dark: '#007749', // forest green
      contrastText: '#ffffff',
      underbar: '#F6F8EA', // grey-green
    },
    secondary: {
      main: '#007749', // forest green
    },
    error: {
      main: '#d81118', // bright red
    },
    success: {
      main: '#008000', // dark green
    },
    custom: {
      accounts: '#f5ba16', // yellow
      appointments: '#0c75a5', // cerulean
      contacts: '#00aaa6', // cyan
      details: '#0c75a5', // cerulean
      maintenance: '#1a8a72', // teal
      letters: '#3E6081', // dark blue/grey
      wellbeing: '#3FA333', // emerald green
      support: '#3FA333', // emerald green
      consented: '#007749', // forest green
      tenancy: '#369883', // green / blue
      notifications: '#E4762D',
    },
    background: {
      lighter: '#ffffff',
      light: '#ececec',
      dark: '#e1e1e1',
    },
    border: {
      lightest: '#eaeaea',
      lighter: '#ededed',
      light: '#d2d2d2',
      grey: '#666666',
      dark: '#3C3C3C',
      field: '#0000003b',
    },
    text: {
      white: '#ffffff',
      lightGrey: '#B8B8B8',
      grey: '#666666',
      darkGrey: textColorDark,
      dark: '#3C3C3C',
    },
    underline: {
      light: '#b2b2b2',
      dark: '#3c3c3c',
    },
  },
  typography: {
    fontFamily: 'Calibre !important',
    useNextVariants: true,
    h1: {
      color: textColorDark,
      fontFamily: 'Calibre',
      fontSize: '1.375rem',
      fontWeight: 600,
      lineHeight: 1.3,
    },
    h2: {
      color: textColorDark,
      fontFamily: 'Calibre',
      fontSize: '1.5rem',
      fontWeight: 600,
      lineHeight: 1.3,
    },
    h3: {
      color: textColorDark,
      fontFamily: 'Calibre',
      fontSize: '1.375rem',
      fontWeight: 600,
      lineHeight: 1.3,
    },
    h4: {
      color: textColorDark,
      fontFamily: 'Calibre',
      fontSize: '1.3125rem',
      fontWeight: 600,
      lineHeight: 1.4115,
    },
    body1: {
      color: textColorDark,
      fontFamily: 'Calibre',
      fontSize: '1.0625rem',
      fontWeight: 400,
      lineHeight: 1.4,
    },
    body2: {
      color: textColorDark,
      fontFamily: 'Calibre',
      fontSize: '1.0625rem',
      fontWeight: 400,
      lineHeight: 1.4,
    },
  },
  variables: {
    borderRadius: 5,
    fieldHeightSmall: 8,
    fieldHeight: 15,
  },
  props: {
    MuiButtonBase: {
      disableRipple: true,
    },
    MuiPrivateSwitchBase: {
      disableRipple: false,
    },
  },
});

theme.overrides = {
  MuiTypography: {
    h1: {
      [theme.breakpoints.up('sm')]: {
        fontSize: '1.5625rem',
        lineHeight: 1.3,
      },
      [theme.breakpoints.up('md')]: {
        fontSize: '2rem',
        lineHeight: 1.3,
      },
    },
    h2: {
      [theme.breakpoints.up('sm')]: {
        fontSize: '1.6875',
        lineHeight: 1.3,
      },
      [theme.breakpoints.up('md')]: {
        fontSize: '1.8125rem',
        lineHeight: 1.3,
      },
    },
    h3: {
      [theme.breakpoints.up('sm')]: {
        fontSize: '1.5rem',
        lineHeight: 1.3,
      },
      [theme.breakpoints.up('md')]: {
        fontSize: '1.625rem',
        lineHeight: 1.3,
      },
    },
    h4: {
      [theme.breakpoints.up('sm')]: {
        fontSize: '1.375rem',
        lineHeight: 1.35,
      },
      [theme.breakpoints.up('md')]: {
        fontSize: '1.5rem',
        lineHeight: 1.35,
      },
    },
    body1: {
      [theme.breakpoints.up('sm')]: {
        fontSize: '1.125rem',
        lineHeight: 1.4,
      },
      [theme.breakpoints.up('md')]: {
        fontSize: '1.1875rem',
        lineHeight: 1.4,
      },
    },
    body2: {
      [theme.breakpoints.up('sm')]: {
        fontSize: '1.125rem',
        lineHeight: 1.4,
      },
      [theme.breakpoints.up('md')]: {
        fontSize: '1.1875rem',
        lineHeight: 1.4,
      },
    },
  },
  MuiPaper: {
    elevation1: {
      boxShadow:
        '0px 1px 3px 0px rgba(0,0,0,0.2), 0px 1px 1px 0px rgba(0,0,0,0.14), 0px 2px 18px -1px rgba(0,0,0,0.12)',
    },
  },
  MuiCard: {
    root: {
      overflow: 'visible',
      boxShadow: 'none',
      borderWidth: '1px',
      borderStyle: 'solid',
      borderColor: theme.palette.border.light,
      borderRadius: 0,
    },
  },
  MuiCardContent: {
    root: {
      '&:last-child': {
        paddingBottom: theme.spacing.unit * 3.75,
      },
      '& a': {
        textDecoration: 'none',
      },
    },
  },
  MuiCardHeader: {
    root: {
      width: '90%',
      overflowWrap: 'break-word',
      wordWrap: 'break-word',
      padding: `
        ${theme.spacing.unit}px
        ${theme.spacing.unit * 2}px
        ${theme.spacing.unit * 2}px
      `,
      [theme.breakpoints.up('md')]: {
        paddingTop: theme.spacing.unit * 2,
        paddingLeft: theme.spacing.unit * 3,
        paddingRight: theme.spacing.unit * 3,
      },
    },
    content: {
      width: 'calc(100% - 142px)',
    },
    title: {
      width: '100%',
      overflowWrap: 'break-word',
      wordWrap: 'break-word',
      marginTop: theme.spacing.unit,
      [theme.breakpoints.up('md')]: {
        marginTop: theme.spacing.unit * 2,
      },
    },
    avatar: {
      borderStyle: 'solid',
      borderWidth: '1px',
      borderColor: theme.palette.border.lighter,
      borderRadius: '100px',
    },
  },
  MuiDialog: {
    paper: {
      minWidth: 310,
      [theme.breakpoints.up('sm')]: {
        minWidth: 350,
      },
    },
  },
  MuiDialogTitle: {
    root: {
      paddingBottom: 0,
    },
  },
  MuiDialogContent: {
    root: {
      textAlign: 'center',
      padding: `0 ${theme.spacing.unit * 2}px ${theme.spacing.unit * 3}px ${
        theme.spacing.unit * 2
      }px`,
      [theme.breakpoints.up('sm')]: {
        padding: `0 ${theme.spacing.unit * 5}px ${theme.spacing.unit * 3}px ${
          theme.spacing.unit * 5
        }px`,
      },
    },
  },
  MuiDialogActions: {
    root: {
      margin: `0 ${theme.spacing.unit * 2}px ${theme.spacing.unit * 4}px`,
      [theme.breakpoints.up('sm')]: {
        marginLeft: theme.spacing.unit * 5,
        marginRight: theme.spacing.unit * 5,
      },
    },
    action: {
      margin: 0,
    },
  },
  MuiFab: {
    primary: {
      backgroundColor: theme.palette.primary.dark,
    },
    sizeMedium: {
      minWidth: 40,
      height: 40,
      width: 40,
      [theme.breakpoints.up('sm')]: {
        minWidth: 56,
        height: 56,
        width: 56,
      },
    },
  },
  MuiButton: {
    root: {
      textTransform: 'none',
      color: theme.palette.primary.dark,
      fontSize: '1.125rem',
      [theme.breakpoints.down('xs')]: {
        minHeight: 35,
      },
    },
    fab: {
      width: 64,
      height: 64,
      fontSize: '1.5rem',
      backgroundColor: theme.palette.primary.dark,
    },
    flatPrimary: {
      color: theme.palette.primary.dark,
      borderRadius: theme.variables.borderRadius,
    },
    contained: {
      [theme.breakpoints.down('xs')]: {
        paddingTop: 0,
        paddingBottom: 0,
      },
    },
    outlined: {
      border: `1px solid ${theme.palette.border.light}`,
      borderRadius: theme.variables.borderRadius,
      color: theme.palette.text.dark,
      '&:hover': {
        color: theme.palette.text.white,
        backgroundColor: theme.palette.primary.dark,
      },
      [theme.breakpoints.down('xs')]: {
        paddingTop: 0,
        paddingBottom: 0,
      },
    },
    outlinedPrimary: {
      border: `1px solid ${theme.palette.primary.dark}`,
      borderRadius: theme.variables.borderRadius,
      color: theme.palette.text.dark,
      // More specific selector to ensure the default background property
      // is overridden.
      '&&:hover': {
        color: theme.palette.text.white,
        backgroundColor: theme.palette.primary.dark,
      },
    },
    raisedPrimary: {
      backgroundColor: theme.palette.primary.dark,
      borderRadius: theme.variables.borderRadius,
      '&:hover': {
        backgroundColor: '#44b038', // emerald green
      },
      // Stop non hover devices changing hover colour. (https://stackoverflow.com/a/42941975)
      '@media (hover: none)': {
        '&:hover': {
          backgroundColor: theme.palette.primary.dark,
        },
      },
    },
  },
  MuiButtonBase: {
    root: {
      fontFamily: 'Calibre',
      fontSize: '1.125rem',
      lineHeight: 1.21,
      textTransform: 'none',
      verticalAlign: 'middle',
      '&:hover': {
        cursor: 'pointer',
      },
      '&:focus': {
        outline: `auto 2px ${theme.palette.primary.dark}`,
      },
    },
  },
  MuiSvgIcon: {
    root: {
      fontSize: '1.5rem',
      [theme.breakpoints.up('md')]: {
        fontSize: '2rem',
      },
    },
  },
  MuiTab: {
    root: {
      textTransform: 'none',
      height: 60,
      maxWidth: 'none',
      '&:focus': {
        outline: `none`,
      },
    },
    label: {
      fontSize: '1rem',
      fontFamily: 'Calibre',
      fontWeight: 600,
      lineHeight: 1.1875,
      [theme.breakpoints.up('md')]: {
        fontSize: '1.1875rem',
        lineHeight: 1.3158,
      },
    },
    labelWrapped: {
      fontSize: '1.188rem',
      [theme.breakpoints.down('md')]: {
        fontSize: '1rem',
      },
    },
  },
  MuiPrivateTabIndicator: {
    root: {
      display: 'none',
    },
  },
  MuiTableRow: {
    head: {
      height: 40,
    },
  },
  MuiTable: {
    root: {
      borderCollapse: 'separate',
    },
  },
  MuiTableCell: {
    root: {
      borderBottom: '1px solid #E1E1E1',
      paddingLeft: theme.spacing.unit,
      paddingRight: theme.spacing.unit,
      height: 40,
      borderCollapse: 'separate',
      '&:last-child': {
        paddingRight: theme.spacing.unit,
      },
    },
    head: {
      borderTop: '1px solid #333333',
      borderBottom: '1px solid #333333',
      fontSize: '0.8125rem',
      color: '#333333',
      textTransform: 'uppercase',
    },
    body: {
      fontSize: '0.875rem',
    },
  },
  MuiExpansionPanel: {
    root: {
      boxShadow: 'none',
      transition: 'none',
      '&:before': {
        backgroundColor: 'none',
      },
    },
    expanded: {
      margin: 0,
    },
  },
  MuiExpansionPanelSummary: {
    root: {
      minHeight: 0,
      padding: '0',
      '&:focus': {
        backgroundColor: 'transparent',
      },
    },
    expanded: {
      '&&': {
        minHeight: 0,
      },
    },
    expandIcon: {
      color: theme.palette.primary.dark,
      border: `1px solid ${theme.palette.primary.dark}`,
      borderRadius: 5,
      right: 0,
      width: 32,
      height: 32,
      top: 'initial',
      bottom: 6,
      transform: 'none',
      transition: 'none',
      padding: 0,
      '&$expanded': {
        transition: 'none',
        transform: 'rotate(180deg)',
      },
    },
    content: {
      width: '100%',
      '&$expanded': {
        margin: '12px 0',
      },
    },
  },
  MuiLink: {
    root: {
      color: theme.palette.primary.dark,
    },
  },
  MuiInputBase: {
    multiline: {
      paddingTop: 2,
    },
  },
  MuiInput: {
    root: {
      color: theme.palette.text.dark,
      '& input[type=number]': {
        '-moz-appearance': 'textfield',
      },
      '& input[type=number]::-webkit-outer-spin-button': {
        '-webkit-appearance': 'none',
        margin: 0,
      },
      '& input[type=number]::-webkit-inner-spin-button': {
        '-webkit-appearance': 'none',
        margin: 0,
      },
      '& input::-webkit-input-placeholder, & textarea::-webkit-input-placeholder':
        {
          color: theme.palette.text.grey,
          opacity: 1,
          position: 'relative',
          top: -2,
          display: 'block',
        },
      '& input:-ms-input-placeholder, & textarea:-ms-input-placeholder': {
        color: theme.palette.text.grey,
        opacity: 1,
      },
      '& input::-moz-placeholder, & textarea::-moz-placeholder': {
        color: theme.palette.text.grey,
        opacity: 1,
      },
      '& input::-ms-clear, & textarea::-ms-clear': {
        width: 0,
        height: 0,
      },
      '& textarea': {
        overflowY: 'auto',
        lineHeight: 1,
      },
    },
    underline: {
      '&:before': {
        borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
      },
      '&:hover:before': {
        borderWidth: '1px !important',
      },
    },
  },
  MuiFormLabel: {
    root: {
      marginTop: `${theme.spacing.unit * 0.75}px`,
      marginBottom: `${theme.spacing.unit * 2.25}px`,
      color: theme.palette.text.dark,
      fontSize: '1.25rem !important',
    },
  },
  MuiInputBase: {
    root: {
      fontSize: '1.375rem !important',
    },
  },
  MuiFormControlLabel: {
    root: {
      marginLeft: 0,
      marginRight: 0,
      borderBottom: `1px solid ${theme.palette.border.light}`,
      '&:first-of-type': {
        borderTop: `1px solid ${theme.palette.border.light}`,
      },
    },
  },
  MuiFormHelperText: {
    error: {
      '&&': {
        fontSize: '1rem',
        color: theme.palette.error.main,
        marginLeft: 0,
      },
    },
  },
  MuiRadio: {
    root: {
      marginLeft: `-${theme.spacing.unit * 1.75}px`,
      marginTop: theme.spacing.unit * 0.625,
      marginBottom: theme.spacing.unit * 0.625,
      '& svg': {
        fontSize: '1.5rem',
      },
    },
  },
  MuiCheckbox: {
    root: {
      '& svg': {
        fontSize: '1.75rem',
      },
    },
  },
  MuiOutlinedInput: {
    input: {
      height: theme.variables.fieldHeightSmall,
      fontSize: '1.0625rem',
      [theme.breakpoints.up('sm')]: {
        height: theme.variables.fieldHeight,
      },
      [theme.breakpoints.up('md')]: {
        fontSize: '1.1875rem',
      },
    },
    inputMultiline: {
      height: 'auto',
    },
    focused: {
      '&& $notchedOutline': {
        borderColor: theme.palette.primary.dark,
      },
    },
  },
  MuiSelect: {
    selectMenu: {
      display: 'inline-flex',
      alignItems: 'center',
      position: 'relative',
      height: theme.variables.fieldHeightSmall,
      minHeight: 0,
      width: 'calc(100% - 46px)',
      [theme.breakpoints.up('sm')]: {
        height: theme.variables.fieldHeight,
      },
      '& + input + svg': {
        top: 10,
        right: 8,
      },
    },
  },
};

export default theme;
